import { useCallback, useMemo, useState } from 'react';

import { Consolidation } from '@futrli-sdk/consolidations';
import { readPersistedFilters, useListFilterPersistence } from '@futrli/predict/utils/list-filters';

const CONSOLIDATIONS_STORAGE_KEY = 'futrli:consolidations-list-filters';

interface ConsolidationsListFilters {
  presentationCurrencyFilterValue: string;
}

const matchesSearch = (consolidation: Consolidation, searchTerm: string): boolean => {
  if (!searchTerm) {
    return true;
  }

  return consolidation.name.toLowerCase().includes(searchTerm.toLowerCase());
};

const matchesPresentationCurrency = (
  consolidation: Consolidation,
  presentationCurrencyFilterValue: string
): boolean => {
  if (presentationCurrencyFilterValue === 'all') {
    return true;
  }

  return consolidation.display_currency === presentationCurrencyFilterValue;
};

export const useConsolidationsFilters = (consolidations: Consolidation[]) => {
  const [searchTerm, setSearchTerm] = useState('');
  const persisted = readPersistedFilters(CONSOLIDATIONS_STORAGE_KEY, '');

  const [presentationCurrencyFilterValue, setPresentationCurrencyFilterValue] = useState(() => {
    return (persisted?.filters?.presentationCurrencyFilterValue as string) ?? 'all';
  });

  const onSearchChange = useCallback((value: string) => setSearchTerm(value), []);
  const onPresentationCurrencyFilterChange = useCallback(
    (value: string) => setPresentationCurrencyFilterValue(value),
    []
  );

  const filters = useMemo<ConsolidationsListFilters>(() => ({ presentationCurrencyFilterValue }), [
    presentationCurrencyFilterValue
  ]);

  const handleRestore = useCallback((restoredFilters: ConsolidationsListFilters) => {
    setPresentationCurrencyFilterValue(restoredFilters.presentationCurrencyFilterValue ?? 'all');
  }, []);

  useListFilterPersistence({
    storageKey: CONSOLIDATIONS_STORAGE_KEY,
    organisationId: '',
    filters,
    sort: undefined,
    onRestore: handleRestore
  });

  const filteredConsolidations = useMemo(
    () =>
      consolidations.filter(
        consolidation =>
          matchesSearch(consolidation, searchTerm) &&
          matchesPresentationCurrency(consolidation, presentationCurrencyFilterValue)
      ),
    [consolidations, searchTerm, presentationCurrencyFilterValue]
  );

  return {
    filteredConsolidations,
    onSearchChange,
    onPresentationCurrencyFilterChange,
    searchTerm,
    presentationCurrencyFilterValue
  };
};
